import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateTrigger } from './useCreateTrigger';
import { createWebhookTriggerConfiguration } from '../TriggerTypeSelector/utils';
import { convertConditionsToExpression } from './filter-utils';
import type {
  IWebhookTriggerSaveData,
  IUseCreateWebhookTriggerProps,
  IUseCreateWebhookTriggerReturn,
} from './types';

/**
 * Hook for creating webhook triggers.
 *
 * This is a wrapper around the generic useCreateTrigger hook that handles
 * webhook-specific data transformation (converting configurationValues to BDK format).
 *
 * @param props - Hook props
 * @returns Object containing createWebhookTrigger function and isLoading state
 *
 * @example
 * const { createWebhookTrigger, isLoading } = useCreateWebhookTrigger({
 *   automationId: 'automation-123',
 *   onSuccess: () => closeDialog(),
 * });
 */
export function useCreateWebhookTrigger({
  automationId,
  isDraft = true,
  onSuccess,
}: IUseCreateWebhookTriggerProps): IUseCreateWebhookTriggerReturn {
  const { t } = useTranslation('automation-inputs');

  // Use the generic createTrigger hook
  const { createTrigger, isLoading } = useCreateTrigger({
    automationId,
    isDraft,
    onSuccess,
    toastMessages: {
      success: t('webhookDialog.toast.success'),
      error: t('webhookDialog.toast.error'),
      errorNoTriggerId: t('webhookDialog.toast.errorNoTriggerId'),
    },
    sentryTags: {
      component: 'useCreateWebhookTrigger',
      action: 'createWebhookTrigger',
    },
  });

  /**
   * Creates a webhook trigger by transforming webhook-specific data
   * and calling the generic createTrigger function.
   *
   * @param data - Webhook trigger save data
   */
  const createWebhookTrigger = useCallback(
    async (data: IWebhookTriggerSaveData) => {
      const {
        selectedIntegration,
        selectedIntegrationVersion,
        selectedConnection,
        selectedEvent,
        inputMappings,
        configurationValues,
        filterConditions,
      } = data;

      // Validate required fields
      if (
        !selectedIntegration ||
        !selectedIntegrationVersion ||
        !selectedConnection ||
        !selectedEvent
      ) {
        return;
      }

      // Convert configuration values to BDK format
      const configuration =
        createWebhookTriggerConfiguration(configurationValues);

      // Build the filter expression from the filter conditions (undefined when empty)
      const filterExpression = convertConditionsToExpression(filterConditions);

      // Call the generic createTrigger
      await createTrigger({
        bookName: selectedIntegration,
        bookVersion: selectedIntegrationVersion,
        connectionId: selectedConnection,
        triggerId: selectedEvent,
        configuration,
        filterExpression,
        inputMappings,
      });
    },
    [createTrigger],
  );

  return {
    createWebhookTrigger,
    isLoading,
  };
}
